import React from 'react';

/**
 * Standard skeleton primitives. Every list/profile/stats screen uses
 * these so loading UI is consistent across the app and the page layout
 * stays stable while data resolves (no jump on first paint).
 *
 * Animation: the existing `wr-skel` class in wordrush-design.css already
 * provides a soft shimmer. These components just compose into the right
 * shapes for our common surfaces.
 */

export function SkeletonRow({ height = 56 }: { height?: number }) {
  return <div className="wr-skel" style={{ height, borderRadius: 16 }} />;
}

export function SkeletonCard({ height = 120 }: { height?: number }) {
  return <div className="wr-skel" style={{ height, borderRadius: 18 }} />;
}

export function SkeletonAvatar({ size = 40 }: { size?: number }) {
  return <div className="wr-skel" style={{ width: size, height: size, borderRadius: '50%' }} />;
}

export function SkeletonText({ width = 100, height = 14 }: { width?: number | string; height?: number }) {
  return <div className="wr-skel" style={{ width, height, borderRadius: 6 }} />;
}

/** A list of N skeleton rows — the bread-and-butter loading state. */
export function SkeletonList({ count = 4, rowHeight = 56 }: { count?: number; rowHeight?: number }) {
  return (
    <div className="wr-col gap-2" aria-busy="true" aria-label="Loading">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonRow key={i} height={rowHeight} />
      ))}
    </div>
  );
}

/** Profile-style hero skeleton — avatar + name + small caption. */
export function SkeletonProfileHero() {
  return (
    <div className="card lg" style={{ padding: 18, textAlign: 'center' }} aria-busy="true">
      <div className="wr-row wr-center">
        <SkeletonAvatar size={88} />
      </div>
      <div className="wr-col gap-2 mt-3" style={{ alignItems: 'center' }}>
        <SkeletonText width={140} height={18} />
        <SkeletonText width={200} height={12} />
      </div>
    </div>
  );
}

/** Stats grid (2 columns × N rows) skeleton. */
export function SkeletonStatsGrid({ rows = 3 }: { rows?: number }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }} aria-busy="true">
      {Array.from({ length: rows * 2 }).map((_, i) => (
        <SkeletonCard key={i} height={88} />
      ))}
    </div>
  );
}
